"use client";
import React, { useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { useAuth } from "../contexts/AuthContext";
import { useScreenType } from "../hooks/useScreenType";
import { usePermissions } from "../utils/usePermissions";
import { buildAccessDeniedLoadErrorQuery } from "../utils/errorMessages";
import LoadingAnimation from "../components/common/loadingAnimation";

const loadingUi = (
  <div className="flex items-center justify-center h-screen">
    <div className="ml-4 text-gray-600">
      <LoadingAnimation />
    </div>
  </div>
);

const Home = () => {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const {
    findFirstAccessibleRoute,
    isLoading: permissionsLoading,
    isNoPermissionsError,
    error,
    isRefreshing,
  } = usePermissions();

  const screenType = useScreenType();
  const loginStartedRef = useRef(false);
  const routeStartedRef = useRef(false);

  // If unauthenticated once loading completes, start interactive login (single-flight guarded in auth layer).
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) return;
    if (loginStartedRef.current) return;
    loginStartedRef.current = true;
    void login(router.asPath);
  }, [authLoading, isAuthenticated, login, router.asPath]);

  const readyToRoute = useMemo(() => {
    if (authLoading) return false;
    if (!isAuthenticated) return false;
    if (!router.isReady) return false;
    if (!screenType) return false;
    if (permissionsLoading || isRefreshing) return false;
    return true;
  }, [authLoading, isAuthenticated, isRefreshing, permissionsLoading, router.isReady, screenType]);

  useEffect(() => {
    if (!readyToRoute) return;
    if (routeStartedRef.current) return;
    routeStartedRef.current = true;

    const run = async () => {
      const redirectTarget =
        screenType === "mobile" ? "/mobile-home" : await findFirstAccessibleRoute();

      if (redirectTarget === "/access-denied") {
        if (isNoPermissionsError) {
          await router.replace({
            pathname: "/access-denied",
            query: {
              isPermissionError: "true",
              errorMessage:
                "You don't have any permissions assigned to your account. Please contact your administrator.",
              isNoPermissionsError: "true",
              redirectUrl: "/",
            },
          });
          return;
        }

        if (error) {
          await router.replace({
            pathname: "/access-denied",
            query: buildAccessDeniedLoadErrorQuery(error, { redirectUrl: "/" }),
          });
          return;
        }
      }

      await router.replace(redirectTarget);
    };

    void run();
  }, [
    error,
    findFirstAccessibleRoute,
    isNoPermissionsError,
    readyToRoute,
    router,
    screenType,
  ]);

  return loadingUi;
};

export default Home;
